import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiJson } from "./queryClient";

export type AuthUser = {
  id: string;
  email: string;
  name: string | null;
  isAdmin?: boolean;
  legalConsentVersion?: string | null;
  legalConsentAt?: string | null;
  setupGuideStatus?: string | null;
};

// Antwort des "Code anfordern"-Schritts. "already_registered" -> Frontend
// leitet zur Anmeldung; "mail_not_configured" -> klare Meldung statt stillem
// Fehlschlag; "code_sent" -> weiter zum Code-Schritt.
export type RequestCodeStatus = "code_sent" | "already_registered" | "mail_not_configured";

type AuthContextValue = {
  user: AuthUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  requestRegistrationCode: (email: string) => Promise<RequestCodeStatus>;
  verifyRegistrationCode: (email: string, code: string) => Promise<void>;
  completeRegistration: (input: {
    email: string;
    code: string;
    password: string;
    name?: string;
  }) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /** Aktive Zustimmung zu AGB + Datenschutz (Version) serverseitig speichern. */
  recordConsent: (version: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const qc = useQueryClient();

  async function refresh() {
    try {
      const res = await fetch("/api/auth/user", { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function login(email: string, password: string) {
    const data = await apiJson<{ user: AuthUser }>("POST", "/api/auth/login", { email, password });
    setUser(data.user);
    qc.clear();
    return data.user;
  }

  // Schritt 1: Code anfordern. Es wird noch KEIN Konto angelegt.
  async function requestRegistrationCode(email: string): Promise<RequestCodeStatus> {
    const data = await apiJson<{ status: RequestCodeStatus }>(
      "POST",
      "/api/auth/register/request-code",
      { email },
    );
    return data.status;
  }

  // Schritt 2: Code vorab prüfen (wirft bei ungültigem/abgelaufenem Code).
  async function verifyRegistrationCode(email: string, code: string): Promise<void> {
    await apiJson("POST", "/api/auth/register/verify-code", { email, code });
  }

  // Schritt 3: Konto anlegen (emailVerified=true) + direkter Login.
  async function completeRegistration(input: {
    email: string;
    code: string;
    password: string;
    name?: string;
  }): Promise<AuthUser> {
    const data = await apiJson<{ user: AuthUser }>(
      "POST",
      "/api/auth/register/complete",
      input,
    );
    setUser(data.user);
    qc.clear();
    return data.user;
  }

  async function logout() {
    await apiJson("POST", "/api/auth/logout");
    setUser(null);
    qc.clear();
  }

  async function recordConsent(version: string) {
    const data = await apiJson<{ user: AuthUser }>(
      "POST",
      "/api/auth/legal-consent",
      { version },
    );
    if (data?.user) {
      setUser(data.user);
    } else {
      setUser((prev) =>
        prev
          ? { ...prev, legalConsentVersion: version, legalConsentAt: new Date().toISOString() }
          : prev,
      );
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        login,
        requestRegistrationCode,
        verifyRegistrationCode,
        completeRegistration,
        logout,
        refresh,
        recordConsent,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
